import { Controller } from "@hotwired/stimulus";

interface ActionLink {
  label?: string;
  url?: string;
  icon?: string;
  divider?: boolean;
  modal?: "jquery" | "turbo";
  method?: string;
  confirm?: string;
  warningModal?: { title: string; message: string; confirmLabel?: string };
  checkProxy?: boolean;
}

export default class extends Controller {
  static values = { actionLinks: Array, toolbarNotice: String, secureTallyUrl: String };
  static targets = ["navBar"];

  declare readonly actionLinksValue: ActionLink[];
  declare readonly toolbarNoticeValue: string;
  declare readonly secureTallyUrlValue: string;
  declare readonly navBarTarget: HTMLElement;
  declare readonly hasNavBarTarget: boolean;

  private injectedActionsWrapper: HTMLElement | null = null;
  private injectedNoticeDesktop: HTMLElement | null = null;
  private injectedNoticeMobile: HTMLElement | null = null;
  private actionsBtn: HTMLButtonElement | null = null;
  private actionsMenu: HTMLElement | null = null;
  private outsideClickHandler: ((e: MouseEvent) => void) | null = null;
  private escapeHandler: ((e: KeyboardEvent) => void) | null = null;

  connect() {
    if (this.hasNavBarTarget) {
      this.portalNavBar();
    }
    if (this.actionLinksValue.length > 0) {
      this.buildAndInjectActionMenu();
    }
    if (this.toolbarNoticeValue) {
      this.buildAndInjectToolbarNotice();
    }

    this.outsideClickHandler = (e: MouseEvent) => {
      if (this.actionsMenu && !this.actionsMenu.hidden) {
        if (
          !this.actionsBtn?.contains(e.target as Node) &&
          !this.actionsMenu.contains(e.target as Node)
        ) {
          this.close();
        }
      }
    };
    document.addEventListener("click", this.outsideClickHandler);

    this.escapeHandler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (this.actionsMenu && !this.actionsMenu.hidden) this.close();
      }
    };
    document.addEventListener("keydown", this.escapeHandler);
  }

  disconnect() {
    this.injectedActionsWrapper?.remove();
    this.injectedActionsWrapper = null;
    this.injectedNoticeDesktop?.remove();
    this.injectedNoticeDesktop = null;
    this.injectedNoticeMobile?.remove();
    this.injectedNoticeMobile = null;
    this.actionsBtn = null;
    this.actionsMenu = null;

    if (this.outsideClickHandler) {
      document.removeEventListener("click", this.outsideClickHandler);
      this.outsideClickHandler = null;
    }
    if (this.escapeHandler) {
      document.removeEventListener("keydown", this.escapeHandler);
      this.escapeHandler = null;
    }
  }

  private handleNavCheckProxy(event: Event) {
    if (!this.secureTallyUrlValue) return;
    const { targetHref } = (event as CustomEvent<{ targetHref: string }>).detail;
    this.checkProxyAndRedirect(targetHref);
  }

  private portalNavBar() {
    const navElement = this.navBarTarget.firstElementChild;
    if (!navElement) return;

    navElement.addEventListener(
      "shared--searchable-dropdown-component--searchable-dropdown-component:checkProxy",
      (event) => this.handleNavCheckProxy(event)
    );

    const titleGroup = this.element.nextElementSibling?.querySelector(".ag-grid-component-title-group");
    if (titleGroup) {
      titleGroup.prepend(navElement);
    } else {
      this.navBarTarget.hidden = false;
    }
  }

  private buildAndInjectToolbarNotice() {
    const grid = this.element.nextElementSibling;
    if (!grid) return;

    const makeNotice = (extraClass: string) => {
      const el = document.createElement("span");
      el.className = `result-header__toolbar-notice ${extraClass}`;
      el.innerHTML = this.toolbarNoticeValue;
      return el;
    };

    // Desktop: inject between title-group and search-row inside the quick-filter-bar
    const titleGroup = grid.querySelector(".ag-grid-component-title-group");
    if (titleGroup?.parentElement) {
      this.injectedNoticeDesktop = makeNotice("result-header__toolbar-notice--desktop");
      titleGroup.parentElement.insertBefore(this.injectedNoticeDesktop, titleGroup.nextSibling);
    }

    // Mobile: inject after the entire quick-filter-bar, before the grid
    const quickFilterBar = grid.querySelector(".ag-grid-component-quick-filter-bar");
    if (quickFilterBar?.parentElement) {
      this.injectedNoticeMobile = makeNotice("result-header__toolbar-notice--mobile");
      quickFilterBar.parentElement.insertBefore(this.injectedNoticeMobile, quickFilterBar.nextSibling);
    }
  }

  private buildAndInjectActionMenu() {
    const wrapper = document.createElement("div");
    wrapper.className = "result-header__actions-wrapper";

    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "result-header__actions-btn";
    btn.setAttribute("aria-haspopup", "true");
    btn.setAttribute("aria-expanded", "false");
    btn.innerHTML = `Actions <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>`;
    btn.addEventListener("click", () => this.toggle());
    this.actionsBtn = btn;

    const dropdown = document.createElement("div");
    dropdown.className = "result-header__actions-dropdown";
    dropdown.hidden = true;

    for (const link of this.actionLinksValue) {
      if (link.divider) {
        const hr = document.createElement("hr");
        hr.className = "result-header__actions-divider";
        dropdown.appendChild(hr);
      } else {
        const a = document.createElement("a");
        a.href = link.url ?? "#";
        a.className = "result-header__actions-option";
        // Icon + label mirror the grid's dropdown_actions_cell_renderer, so the same action
        // reads the same here and in a dashboard row menu.
        if (link.icon) {
          const icon = document.createElement("i");
          icon.className = `fas fa-${link.icon}`;
          icon.setAttribute("aria-hidden", "true");
          a.appendChild(icon);
        }
        const label = document.createElement("span");
        label.textContent = link.label ?? "";
        a.appendChild(label);
        if (link.modal === "jquery") a.rel = "modal:open";
        else if (link.modal === "turbo") {
          a.dataset.turbo = "true";
          a.dataset.turboFrame = "modal";
        }
        if (link.checkProxy && this.secureTallyUrlValue) {
          a.addEventListener("click", (e) => {
            e.preventDefault();
            this.close();
            this.checkProxyAndRedirect(link.url!);
          });
        } else if (link.warningModal) {
          a.addEventListener("click", (e) => {
            e.preventDefault();
            this.close();
            this.showWarningModal(
              [{ text: link.warningModal!.message, red: true }],
              link.warningModal!.confirmLabel ?? "OK",
              () => this.submitWithMethod(link.url!, link.method ?? "get"),
              { title: link.warningModal!.title, confirmFirst: true }
            );
          });
        } else {
          if (link.method) a.dataset.method = link.method;
          if (link.confirm) a.dataset.confirm = link.confirm;
        }
        dropdown.appendChild(a);
      }
    }
    this.actionsMenu = dropdown;

    wrapper.appendChild(btn);
    wrapper.appendChild(dropdown);
    this.injectedActionsWrapper = wrapper;

    const searchRow = this.element.nextElementSibling?.querySelector(".ag-grid-component-search-row");
    if (searchRow) {
      searchRow.prepend(wrapper);
    } else if (this.hasNavBarTarget) {
      this.navBarTarget.hidden = false;
      this.navBarTarget.appendChild(wrapper);
    } else {
      wrapper.classList.add("result-header__actions-inline");
      this.element.appendChild(wrapper);
    }
  }

  private toggle() {
    if (!this.actionsMenu) return;
    if (this.actionsMenu.hidden) {
      this.actionsMenu.hidden = false;
      this.actionsBtn?.setAttribute("aria-expanded", "true");
    } else {
      this.close();
    }
  }

  private close() {
    if (!this.actionsMenu) return;
    this.actionsMenu.hidden = true;
    this.actionsBtn?.setAttribute("aria-expanded", "false");
  }

  private checkProxyAndRedirect(targetHref: string) {
    fetch(this.secureTallyUrlValue, { headers: { Accept: "application/json" } })
      .then((r) => r.json())
      .then((data) => {
        if (data.allow_redirect) {
          window.location.href = targetHref;
        } else if (data.low_security_with_proxy) {
          this.showProxyModal(targetHref);
        } else {
          this.showBallotModal(data.votes_remaining, targetHref, data.proxy_opened);
        }
      });
  }

  private showProxyModal(targetHref: string) {
    this.showWarningModal(
      [{ text: "This vote has proxy vote active.\nIf the results are viewed, the administrator won't be able to record the proxy vote.", red: true }],
      "Close Proxy Entry",
      () => { window.location.href = targetHref; },
      { title: "Close Proxy Entry and View Results?", confirmFirst: true }
    );
  }

  private showBallotModal(votesRemaining: number, targetHref: string, proxyOpened: boolean) {
    const messages: Array<{ text: string; red?: boolean }> = [
      { text: `${votesRemaining} voters have not voted. These voters may submit paper ballots.` },
      { text: "If results are viewed, no more paper ballots can be recorded by administrators", red: true },
    ];
    if (proxyOpened) {
      messages.push({ text: "This vote has proxy vote active.\nIf the results are viewed, the administrator won't be able to record the proxy vote.", red: true });
    }
    this.showWarningModal(messages, "Close Paper Ballot Entry", () => { window.location.href = targetHref; }, {
      title: "Close Paper Ballot Entry and View Results?",
      confirmFirst: true,
    });
  }

  private showWarningModal(
    messages: Array<{ text: string; red?: boolean }>,
    actionLabel: string,
    onConfirm: () => void,
    { title = "Warning", confirmFirst = false }: { title?: string; confirmFirst?: boolean } = {}
  ) {
    document.getElementById("proxy-warning-modal-overlay")?.remove();
    document.getElementById("proxy-warning-modal-style")?.remove();

    const style = document.createElement("style");
    style.id = "proxy-warning-modal-style";
    style.textContent = `
      @media (max-width: 620px) {
        #proxy-warning-modal-overlay .toolkit-confirm-delete-modal__actions {
          flex-direction: column;
        }
        #proxy-warning-modal-overlay .toolkit-confirm-delete-modal__actions > * {
          width: 100%;
          white-space: normal;
        }
      }
    `;
    document.head.appendChild(style);

    const overlay = document.createElement("div");
    overlay.id = "proxy-warning-modal-overlay";
    overlay.className = "toolkit-confirm-delete-modal__overlay";

    const dialog = document.createElement("div");
    dialog.className = "toolkit-confirm-delete-modal__dialog";
    dialog.style.maxWidth = "620px";
    dialog.setAttribute("role", "alertdialog");
    dialog.setAttribute("aria-modal", "true");

    const content = document.createElement("div");
    content.className = "toolkit-confirm-delete-modal__content";

    const iconWrap = document.createElement("div");
    iconWrap.className = "toolkit-confirm-delete-modal__icon toolkit-confirm-delete-modal__icon--warning";
    iconWrap.innerHTML = `<i class="fas fa-exclamation-triangle"></i>`;

    const titleEl = document.createElement("h3");
    titleEl.className = "toolkit-confirm-delete-modal__title";
    titleEl.textContent = title;

    content.appendChild(iconWrap);
    content.appendChild(titleEl);

    for (const msg of messages) {
      const p = document.createElement("p");
      p.className = "toolkit-confirm-delete-modal__message";
      p.style.fontSize = "1.5rem";
      if (msg.red) p.style.cssText = "color: #dc2626; font-weight: 500; font-size: 1.5rem;";
      const lines = msg.text.split("\n");
      lines.forEach((line, i) => {
        p.appendChild(document.createTextNode(line));
        if (i < lines.length - 1) p.appendChild(document.createElement("br"));
      });
      content.appendChild(p);
    }

    const actions = document.createElement("div");
    actions.className = "toolkit-confirm-delete-modal__actions";

    const closeModal = () => { overlay.remove(); style.remove(); };

    const cancelBtn = document.createElement("button");
    cancelBtn.className = "toolkit-button-component toolkit-button-component--blue-stroke";
    cancelBtn.type = "button";
    cancelBtn.textContent = "Cancel";
    cancelBtn.addEventListener("click", closeModal);

    const confirmBtn = document.createElement("button");
    confirmBtn.className = "toolkit-button-component toolkit-button-component--orange";
    confirmBtn.type = "button";
    confirmBtn.textContent = actionLabel;
    confirmBtn.addEventListener("click", () => { closeModal(); onConfirm(); });

    if (confirmFirst) {
      actions.appendChild(confirmBtn);
      actions.appendChild(cancelBtn);
    } else {
      actions.appendChild(cancelBtn);
      actions.appendChild(confirmBtn);
    }

    content.appendChild(actions);
    dialog.appendChild(content);
    overlay.appendChild(dialog);

    overlay.addEventListener("click", (e) => { if (e.target === overlay) closeModal(); });

    document.body.appendChild(overlay);
  }

  private submitWithMethod(url: string, method: string) {
    const form = document.createElement("form");
    form.action = url;
    form.method = ["get", "post"].includes(method.toLowerCase()) ? method.toLowerCase() : "post";

    const csrf = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null;
    if (csrf) {
      const tokenInput = document.createElement("input");
      tokenInput.type = "hidden";
      tokenInput.name = "authenticity_token";
      tokenInput.value = csrf.content;
      form.appendChild(tokenInput);
    }

    if (!["get", "post"].includes(method.toLowerCase())) {
      const methodInput = document.createElement("input");
      methodInput.type = "hidden";
      methodInput.name = "_method";
      methodInput.value = method;
      form.appendChild(methodInput);
    }

    document.body.appendChild(form);
    form.submit();
  }
}
